// `StageScript`, `StageLayout` (SuiteCompile/StagePlay.swift; `design-docs/Stage plays — design.md`): a compiled play — its
// scenes in acts as printed, with the pages before them — laid out in US manuscript format: every act and scene from the
// top of a page under its headings, names at 4 in (column 25 of the 1.5 in margin), dialogue the full sixty columns,
// directions and parentheticals indented 1.5 in in brackets, lyrics in capitals; speeches broken only at a sentence's
// end with two lines either side, the rest under "NAME (Continued)". A play's sample (`render.screenplay.sample`) is cut
// from it here too.
import { BookMatter, fountainParagraphs, fountainParagraphText, fountainTitlePage, plainText, serialize, songHeading, songsIn, type Song, type StoryEntity } from '@writing-suite/core';
import { shortName } from '@writing-suite/core/story';
import { authorCredits, numberWords, pageInches, roman } from '../manuscript.ts';
import type { CompiledManuscript, ScreenplaySample } from '../types.ts';
import {
  BOTTOM_MARGIN, count, cut, length, run, sentenceEnds, speaker, TOP_MARGIN, WIDTH, wrap,
  type Element, type Paragraph, type Run, type ScriptLine, type ScriptPage, type TitleField,
} from './script.ts';

const blank = (s: string) => /^\p{White_Space}*$/u.test(s);
/** `trimmingCharacters(in: .whitespaces)`. */
const trimSpaces = (s: string) => s.replace(/^[\p{Zs}\t]+|[\p{Zs}\t]+$/gu, '');
const cloneParagraph = (p: Paragraph): Paragraph => ({ ...p, lines: p.lines.map((l) => l.map((r) => ({ ...r }))) });

// MARK: StageScript

/** A scene (or an act's own opening text): it starts a new page under its headings. */
export interface StagePart {
  /** The act's number, null in a play without acts or an act that takes none (a prologue). */
  act: number | null;
  /** The scene's number; null when its act has only the one scene. */
  scene: number | null;
  /** "ACT I", "SCENE 2" (or an unnumbered act's title), over the scene. */
  headings: string[];
  paragraphs: Paragraph[];
  /** "END OF ACT ONE", "END OF PLAY" after it (preset `endMarkers`). */
  end: string | null;
  /** The document it came from and its title, which a sample can start at. */
  fragmentID?: string | null;
  title?: string | null;
  /** Its act's heading, which a sample starting part-way through the act puts over its first scene. */
  actHeading?: string | null;
}

export interface CastEntry { name: string; description: string }

export interface StageScript {
  title: string;
  /** Fountain title-page fields at the top of the first scene, which win over the generated page. */
  titlePage: TitleField[];
  /** The Title Page document's paragraphs, when the writer has changed it from the template. */
  titleLines: string[] | null;
  /** "A Play in Two Acts", from the act count. */
  description: string;
  cast: CastEntry[];
  /** Paragraphs of a written cast page that aren't a name and a description (doubling, cast size). */
  castNotes: string[];
  setting: string[];
  parts: StagePart[];
  /** A musical (`CompiledManuscript.musical`): its songs headed and numbered, a Musical Numbers page, credits by role. */
  musical: boolean;
  /** A musical's songs in order, each with the heading of the act it's in. */
  songs: SongEntry[];
  /** The Musical Numbers page as the writer wrote it (null: made from `songs`). */
  musicalNumbers: string[] | null;
  /** The title page's credits by role, a musical's when anyone has a role. */
  credits: string[] | null;
}

export interface SongEntry { song: Song; act: string | null }

/** Groups that never name a character: "ALL", "BOTH", "MARA AND TOM". */
const GROUP_CUES = new Set(['ALL', 'BOTH', 'EVERYONE', 'TOGETHER', 'OMNES', 'THE OTHERS', 'OTHERS']);

export const emptyStageScript = (): StageScript =>
  ({ title: '', titlePage: [], titleLines: null, description: '', cast: [], castNotes: [], setting: [], parts: [],
    musical: false, songs: [], musicalNumbers: null, credits: null });

/** `StageScript.init(_ m:)`. */
export function stageScript(m: CompiledManuscript): StageScript {
  const settings = m.preset.render.screenplay;
  const sections = m.sections.filter((s) => s.matter === null);
  const sources = sections.flatMap((s) => (s.source !== null && !blank(s.source) ? [s.source] : []));
  const titlePage = sources.length ? fountainTitlePage(sources[0]).map((f) => ({ key: f.key, value: f.value })) : [];

  // Acts are top-level folders (parts); scenes are the documents in them, or the play's own when it has none.
  interface Scene { source: string; opening: boolean; fragmentID: string | null; title: string }
  interface Act { title: string | null; number: number | null; scenes: Scene[] }
  let acts: Act[] = [];
  let actCount = 0;
  for (const section of sections) {
    const written = section.source !== null && !blank(section.source) ? section.source : null;
    const numbered = section.numbered ?? true;
    switch (section.role) {
      case 'part':
        if (numbered) actCount += 1;
        acts.push({ title: section.title, number: numbered ? actCount : null, scenes: [] });
        if (written !== null) acts[acts.length - 1].scenes.push({ source: written, opening: true, fragmentID: section.fragmentID, title: section.title });
        break;
      case 'chapter': case 'scene':
        if (!acts.length) acts.push({ title: null, number: null, scenes: [] });
        if (written !== null) acts[acts.length - 1].scenes.push({ source: written, opening: false, fragmentID: section.fragmentID, title: section.title });
        break;
      default: break;
    }
  }
  // An act with nothing written yet isn't printed, nor counted in "A Play in Two Acts".
  acts = acts.filter((a) => a.scenes.length > 0);
  const hasActs = acts.some((a) => a.title !== null);
  const actsWritten = m.playActs ?? acts.filter((a) => a.number !== null).length;
  const musical = m.musical ?? false;
  const description = `A ${musical ? 'Musical' : 'Play'} in ${numberWords(Math.max(1, actsWritten))} Act${actsWritten > 1 ? 's' : ''}`;
  const credits = musical && m.author ? authorCredits(m.author) : null;
  const songs: SongEntry[] = [];

  const parts: StagePart[] = [];
  let sceneCount = 0;
  acts.forEach((act, a) => {
    if (settings?.continuousScenes !== true) sceneCount = 0;
    const shown = act.scenes.filter((s) => !s.opening).length > 1;
    const actHeading = hasActs && act.title !== null ? (act.number !== null ? `ACT ${roman(act.number)}` : act.title.toUpperCase()) : null;
    act.scenes.forEach((scene, s) => {
      const headings: string[] = [];
      if (s === 0 && actHeading !== null) headings.push(actHeading);
      let number: number | null = null;
      if (!scene.opening) {
        sceneCount += 1;
        if (shown) {
          number = sceneCount;
          headings.push(`SCENE ${sceneCount}`);
        }
      }
      let paragraphs = fountainParagraphs(scene.source).map(cloneParagraph);
      if (musical) {
        const sung = sing(paragraphs, songs.length + 1, settings?.format === 'stage-uk');
        paragraphs = sung.paragraphs;
        songs.push(...sung.songs.map((song) => ({ song, act: actHeading })));
      }
      // A US play names a returning speaker again "(Continued)"; a UK play goes on without the name.
      if ((settings?.continueds ?? true) && settings?.format !== 'stage-uk') addContinueds(paragraphs, ' (Continued)');
      let end: string | null = null;
      if (settings?.endMarkers === true && s === act.scenes.length - 1) {
        if (a === acts.length - 1) end = 'END OF PLAY';
        else if (act.number !== null) end = `END OF ACT ${numberWords(act.number).toUpperCase()}`;
      }
      parts.push({ act: hasActs ? act.number : null, scene: number, headings, paragraphs, end,
        fragmentID: scene.fragmentID, title: scene.title, actHeading });
    });
  });

  // The pages before the play: the writer's, where written.
  const matter = (kind: BookMatter) => m.sections.find((s) => s.matter === kind);
  const lines = (blocks: CompiledManuscript['sections'][number]['blocks']) => blocks.map((b) => plainText(serialize([b]))).filter((t) => t !== '');
  let titleLines: string[] | null = null;
  const titlePageDoc = matter('title-page');
  if (titlePageDoc && titlePageDoc.source !== null && titlePageDoc.source.replace(/^\p{White_Space}+|\p{White_Space}+$/gu, '') !== BookMatter.template('title-page')) {
    titleLines = lines(titlePageDoc.blocks);
  }
  const settingDoc = matter('setting');
  const setting = settingDoc ? lines(settingDoc.blocks) : [];
  let cast: CastEntry[] = [], castNotes: string[] = [];
  const castDoc = matter('cast-list');
  if (castDoc) {
    const written = lines(castDoc.blocks);
    if (written.length === 0) cast = castOf(parts, m.characters ?? []);
    else [cast, castNotes] = castPage(written);
  }
  let musicalNumbers: string[] | null = null;
  const numbersDoc = musical ? matter('musical-numbers') : undefined;
  if (numbersDoc) {
    const written = lines(numbersDoc.blocks);
    musicalNumbers = written.length ? written : null;
  }
  return { title: m.title, titlePage, titleLines, description, cast, castNotes, setting, parts, musical, songs, musicalNumbers, credits };
}

/** `StageScript.sing`: a musical's scene as printed — each song's label its heading, centred and numbered from `first`
 *  ("#3 — THE RIVER'S HIGH"; bold in a UK play), anything written under the label kept as a direction; music cues and
 *  lyrics in capitals. */
export function sing(paragraphs: Paragraph[], first: number, uk: boolean): { paragraphs: Paragraph[]; songs: Song[] } {
  const found = songsIn(paragraphs);
  const out = [...paragraphs];
  const songs: Song[] = [];
  for (let n = found.length - 1; n >= 0; n--) {
    const s = found[n];
    const song: Song = { number: first + n, title: s.title, singers: s.singers };
    songs.unshift(song);
    const replacement: Paragraph[] = [{ element: 'centered', lines: [[run(songHeading(song), { bold: uk })]], isDual: false }];
    const rest = out[s.index].lines.slice(1);
    if (rest.length) replacement.push({ element: 'action', lines: rest, isDual: false });
    out.splice(s.index, 1, ...replacement);
  }
  const capitals = (p: Paragraph): Paragraph => ({ ...p, lines: p.lines.map((l) => l.map((r) => ({ ...r, text: r.text.toUpperCase() }))) });
  return { paragraphs: out.map((p) => (p.element === 'lyric' || (p.element === 'action' && isMusicCue(fountainParagraphText(p))) ? capitals(p) : p)), songs };
}

/** `StageScript.isMusicCue`: a direction where music comes in: "MUSIC:", "DANCE:", "UNDERSCORE…", in any case. */
export function isMusicCue(text: string): boolean {
  let t = trimSpaces(text);
  if (t.startsWith('!')) t = t.slice(1);
  if (t.startsWith('(')) t = t.slice(1);
  const lower = t.toLowerCase();
  return lower.startsWith('music:') || lower.startsWith('dance:') || lower.startsWith('underscore');
}

/** `StageScript.numbersPage`: each act's heading and its songs. */
export function numbersPage(script: StageScript): { act: string | null; songs: Song[] }[] {
  const out: { act: string | null; songs: Song[] }[] = [];
  for (const e of script.songs) {
    const last = out[out.length - 1];
    if (last && last.act === e.act) last.songs.push(e.song); else out.push({ act: e.act, songs: [e.song] });
  }
  return out;
}

/** `StageScript.cast(of:characters:)`: who speaks, most speeches first (then by first appearance): the cast's own name and
 *  role where a cue names one of its characters (by name, short name or alias), else the cue as written. */
export function castOf(parts: readonly StagePart[], characters: readonly StoryEntity[]): CastEntry[] {
  const order: string[] = [];
  const counts = new Map<string, number>();
  const entries = new Map<string, CastEntry>();
  const key = (s: string) => trimSpaces(s.toUpperCase());
  for (const p of parts.flatMap((x) => x.paragraphs)) {
    if (p.element !== 'character') continue;
    const name = key(speaker(fountainParagraphText(p)));
    if (name === '' || GROUP_CUES.has(name) || name.includes(' AND ') || name.includes('&') || name.includes('/')) continue;
    const entity = characters.find((e) => [e.name, shortName(e), ...e.aliases].map(key).includes(name));
    const id = entity ? `entity:${entity.id}` : name;
    if (!counts.has(id)) {
      order.push(id);
      entries.set(id, { name: entity ? key(entity.name) : name, description: entity?.role ?? '' });
    }
    counts.set(id, (counts.get(id) ?? 0) + 1);
  }
  return order.map((id, offset) => ({ id, offset }))
    .sort((a, b) => counts.get(b.id)! - counts.get(a.id)! || a.offset - b.offset)
    .map((x) => entries.get(x.id)!);
}

/** `StageScript.castPage`: "MARA: the elder sister" (or a tab or a dash between) is a name and its description; a short
 *  line in capitals is a name alone; anything else is a note printed under the list. */
export function castPage(paragraphs: readonly string[]): [CastEntry[], string[]] {
  const cast: CastEntry[] = [], notes: string[] = [];
  const pattern = /^\s*([^:\t—–]{1,40}?)\s*(?::|\t| — | – | - )\s*(.+)$/u;
  for (const p of paragraphs) {
    const m = pattern.exec(p);
    if (m) cast.push({ name: m[1].toUpperCase(), description: m[2] });
    else if (count(p) <= 40 && p === p.toUpperCase() && /\p{L}/u.test(p)) cast.push({ name: trimSpaces(p), description: '' });
    else notes.push(p);
  }
  return [cast, notes];
}

/** `StageScript.continuations`: which cues bring back the character who spoke last, with only directions between (a
 *  heading, a transition or a page break starts afresh): a UK play leaves the name off them. */
export function continuations(paragraphs: readonly Paragraph[]): boolean[] {
  let last: string | null = null;
  return paragraphs.map((p) => {
    switch (p.element) {
      case 'sceneHeading': case 'transition': case 'pageBreak':
        last = null;
        return false;
      case 'character': {
        const name = speaker(fountainParagraphText(p));
        const result = name === last && !p.isDual;
        last = p.isDual ? null : name;
        return result;
      }
      default: return false;
    }
  });
}

/** `ScreenplayScript.addContinueds(_:marker:)`: a play's "(Continued)" where a screenplay writes "(CONT'D)". */
function addContinueds(paragraphs: Paragraph[], marker: string): void {
  let last: string | null = null;
  for (const p of paragraphs) {
    switch (p.element) {
      case 'sceneHeading': case 'transition': case 'pageBreak': last = null; break;
      case 'character': {
        const cue = fountainParagraphText(p);
        const name = speaker(cue);
        if (name === last && !p.isDual && !cue.toUpperCase().includes('CONT') && p.lines.length) {
          const line = p.lines[0];
          const end = line[line.length - 1];
          if (end && !end.italic && !end.bold && !end.underline) end.text += marker;
          else line.push(run(marker));
        }
        last = p.isDual ? null : name;
        break;
      }
      default: break;
    }
  }
}

/** `StageScript.pageNumber`: the number at the top right of a page of the play: act–scene–page ("II-1-51", "II-51" for an
 *  act of one scene), or plain ("51.", a UK play's "51") when the preset says so or the play has no acts. */
export function stagePageNumber(script: StageScript, page: number, part: number, style: string | undefined, period = true): string {
  const p = script.parts[part];
  if (style === 'plain' || !p || p.act === null) return period ? `${page}.` : `${page}`;
  return [roman(p.act), ...(p.scene !== null ? [String(p.scene)] : []), String(page)].join('-');
}

// MARK: Samples

/** `StageScript.printed(_:pages:)`: the play as a preset prints it, all of it or its sample, cut where `pages` (the pages
 *  a script's scenes print on) runs past the sample's. */
export function printedStageScript(m: CompiledManuscript,
  pages: (s: StageScript) => number | null = (s) => stageLayout(s, pageInches(m.preset.render.page.size).height).pages.length): StageScript {
  const script = stageScript(m);
  const sample = m.preset.render.screenplay?.sample;
  return sample ? sampleOf(script, sample, pages) : script;
}

/** The same with pages counted asynchronously (a UK play's, by Typst). */
export async function printedStageScriptAsync(m: CompiledManuscript, pages: (s: StageScript) => Promise<number | null>): Promise<StageScript> {
  const script = stageScript(m);
  const sample = m.preset.render.screenplay?.sample;
  if (!sample) return script;
  const plan = sampleCuts(script, sample);
  if (!plan.cuts.length || ((await pages(plan.whole)) ?? Infinity) <= sample.pages) return plan.whole;
  let low = -1, high = plan.cuts.length - 1;
  while (low < high) {
    const mid = Math.floor((low + high + 1) / 2);
    if (((await pages(plan.cut(plan.cuts[mid]))) ?? Infinity) <= sample.pages) low = mid; else high = mid - 1;
  }
  return plan.cut(plan.cuts[Math.max(0, low)]);
}

/** `StageScript.sample`: from the scene `from` names (its title, case and accents aside, or its document's id; else the
 *  start), the longest run of whole speeches and directions whose pages number no more than the sample's. The pages
 *  before the play stay the title page alone. */
export function sampleOf(script: StageScript, sample: ScreenplaySample, pages: (s: StageScript) => number | null): StageScript {
  const plan = sampleCuts(script, sample);
  if (!plan.cuts.length || (pages(plan.whole) ?? Infinity) <= sample.pages) return plan.whole;
  let low = -1, high = plan.cuts.length - 1;
  while (low < high) {
    const mid = Math.floor((low + high + 1) / 2);
    if ((pages(plan.cut(plan.cuts[mid])) ?? Infinity) <= sample.pages) low = mid; else high = mid - 1;
  }
  return plan.cut(plan.cuts[Math.max(0, low)]);
}

interface Cut { part: number; paragraph: number }

function sampleCuts(script: StageScript, sample: ScreenplaySample): { whole: StageScript; cuts: Cut[]; cut: (c: Cut) => StageScript } {
  const base: StageScript = { ...script, cast: [], castNotes: [], setting: [], songs: [], musicalNumbers: null };
  let parts = script.parts.map((p) => ({ ...p, headings: [...p.headings] }));
  const from = sample.from !== undefined ? trimSpaces(sample.from) : '';
  if (from !== '') {
    const fold = (s: string) => s.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase();
    const start = parts.findIndex((p) => p.fragmentID === from || (p.title != null && fold(p.title) === fold(from)));
    if (start >= 0) {
      parts = parts.slice(start);
      const act = parts[0].actHeading;
      if (act != null && parts[0].headings[0] !== act) parts[0].headings.unshift(act);
    }
  }
  // Where the play may stop: before a part, or before a paragraph that starts something (not inside a speech, and not
  // between the halves of dual dialogue).
  const inSpeech = new Set<Element>(['dialogue', 'parenthetical', 'lyric']);
  const cuts: Cut[] = [];
  parts.forEach((part, p) => {
    part.paragraphs.forEach((paragraph, i) => {
      if (i === 0 || inSpeech.has(paragraph.element) || (paragraph.element === 'character' && paragraph.isDual)) return;
      cuts.push({ part: p, paragraph: i });
    });
    cuts.push({ part: p, paragraph: part.paragraphs.length });
  });
  const whole: StageScript = { ...base, parts };
  const cut = (c: Cut): StageScript => {
    const kept = parts.slice(0, c.part + 1).map((p) => ({ ...p }));
    if (c.paragraph < kept[c.part].paragraphs.length) {
      kept[c.part].paragraphs = kept[c.part].paragraphs.slice(0, c.paragraph);
      kept[c.part].end = null;
    }
    return { ...base, parts: kept };
  };
  return { whole, cuts, cut };
}

// MARK: Pagination

/** `ScriptLayout.SpeechColumns`, with a play's action and lyric columns. */
export interface SpeechColumns {
  cue: number; cueWidth: number; paren: number; parenWidth: number; dialogue: number; dialogueWidth: number;
  action: number; actionWidth: number; lyric: number | null; lyricWidth: number | null; lyricItalic: boolean;
  /** A sitcom's dialogue: a blank line between its lines (`multicamLayout`). */
  doubleSpaced?: boolean;
}
export const columnsOf = (c: Pick<SpeechColumns, 'cue' | 'cueWidth' | 'paren' | 'parenWidth' | 'dialogue' | 'dialogueWidth'>, more: Partial<SpeechColumns> = {}): SpeechColumns =>
  ({ action: 0, actionWidth: WIDTH, lyric: null, lyricWidth: null, lyricItalic: true, ...c, ...more });
export const STAGE_COLUMNS = columnsOf({ cue: 25, cueWidth: 35, paren: 15, parenWidth: 40, dialogue: 0, dialogueWidth: 60 },
  { action: 15, actionWidth: 40, lyric: 5, lyricWidth: 55, lyricItalic: false });
export const DUAL_LEFT = columnsOf({ cue: 8, cueWidth: 20, paren: 3, parenWidth: 22, dialogue: 0, dialogueWidth: 28 });
export const DUAL_RIGHT = columnsOf({ cue: 39, cueWidth: 21, paren: 34, parenWidth: 22, dialogue: 31, dialogueWidth: 29 });

export interface Piece { column: number; runs: Run[] }
export const piece = (column: number, runs: Run[]): Piece => ({ column, runs });

type SegmentKind = 'action' | 'dialogue' | 'parenthetical' | 'lyric';
export interface Segment { runs: Run[]; kind: SegmentKind }

export function pieces(s: Segment, c: SpeechColumns): Piece[] {
  switch (s.kind) {
    case 'action': return wrap(s.runs, c.actionWidth).map((r) => piece(c.action, r));
    case 'dialogue': return wrap(s.runs, c.dialogueWidth).flatMap((r, i) =>
      [...(c.doubleSpaced && i > 0 ? [piece(c.dialogue, [])] : []), piece(c.dialogue, r)]);
    case 'lyric': return wrap(s.runs.map((r) => ({ ...r, italic: r.italic || c.lyricItalic })), c.lyricWidth ?? c.dialogueWidth)
      .map((r) => piece(c.lyric ?? c.dialogue, r));
    // Wrapped lines sit inside the opening parenthesis.
    case 'parenthetical': return wrap(s.runs, c.parenWidth, 1).map((r, i) => piece(c.paren + (i > 0 ? 1 : 0), r));
  }
}

export interface Speech { name: string; cue: Piece[]; segments: Segment[]; columns: SpeechColumns }
const body = (s: Speech) => s.segments.flatMap((g) => pieces(g, s.columns));
export const speechHeight = (s: Speech) => s.cue.length + body(s).length;
const height = speechHeight;

export type Unit =
  | { type: 'block'; element: Element; lines: Piece[] }
  | { type: 'action'; segments: Segment[] }
  | { type: 'speech'; speech: Speech }
  | { type: 'dual'; left: Speech; right: Speech }
  | { type: 'pageBreak' };

/** `ScriptLayout.lines(pageHeight:)`: lines on a page between 1 in margins. */
export const linesForPage = (pageHeight: number) => Math.max(20, Math.floor(((pageHeight - TOP_MARGIN - BOTTOM_MARGIN) * 72) / 12));

/** `ScriptLayout.Paginator`: fills pages line by line, breaking action and speeches only where `split` allows. */
export class Paginator {
  pages: ScriptLine[][] = [[]];
  row = 0;
  constructor(readonly capacity: number) {}

  room(gap: number) { return this.capacity - this.row - (this.row > 0 ? gap : 0); }

  newPage() {
    if (this.row > 0 || this.pages[this.pages.length - 1].length) this.pages.push([]);
    this.row = 0;
  }

  place(list: Piece[], gap: number) {
    if (this.row > 0) this.row += gap;
    for (const p of list) {
      if (this.row >= this.capacity) this.newPage();
      this.pages[this.pages.length - 1].push({ row: this.row, column: p.column, runs: p.runs, isSceneHeading: false });
      this.row += 1;
    }
  }

  placeAction(segments: Segment[], columns: SpeechColumns, gap: number): void {
    const lines = segments.flatMap((s) => pieces(s, columns));
    if (lines.length <= this.room(gap)) return this.place(lines, gap);
    const c = split(segments, columns, this.room(gap), 2, 2);
    if (c) {
      this.place(c.head.flatMap((s) => pieces(s, columns)), gap);
      this.newPage();
      return this.placeAction(c.tail, columns, 0);
    }
    if (this.row > 0) { this.newPage(); return this.placeAction(segments, columns, 0); }
    this.place(lines, 0); // a page of action with nowhere to break: break where it must
  }

  /** `more` goes under a speech's first half when it breaks (null: nothing); `continued` heads the rest. */
  placeSpeech(s: Speech, gap: number, more: Piece | null, continued: (name: string) => Piece[]): void {
    if (height(s) <= this.room(gap)) return this.place([...s.cue, ...body(s)], gap);
    const contd = s.name === '' ? [] : continued(s.name);
    const c = split(s.segments, s.columns, this.room(gap) - s.cue.length - (more ? 1 : 0), 2, 2);
    if (c) {
      this.place([...s.cue, ...body({ ...s, cue: [], segments: c.head }), ...(more ? [more] : [])], gap);
      this.newPage();
      return this.placeSpeech({ ...s, cue: contd, segments: c.tail }, 0, more, continued);
    }
    if (this.row > 0) { this.newPage(); return this.placeSpeech(s, gap, more, continued); }
    this.place([...s.cue, ...body(s)], 0); // a page-long speech with no sentence to break at
  }

  placeSideBySide(left: Speech, right: Speech, gap: number) {
    const tallest = Math.max(height(left), height(right));
    if (tallest > this.room(gap)) this.newPage();
    if (this.row > 0) this.row += gap;
    for (const column of [left, right]) {
      [...column.cue, ...body(column)].forEach((p, i) => {
        this.pages[this.pages.length - 1].push({ row: this.row + i, column: p.column, runs: p.runs, isSceneHeading: false });
      });
    }
    this.row += tallest;
  }

  finished(): ScriptPage[] {
    const pages = [...this.pages];
    if (pages.length > 1 && pages[pages.length - 1].length === 0) pages.pop();
    return pages.map((lines) => ({ lines }));
  }
}

/** `ScriptLayout.split`. */
function split(segments: Segment[], columns: SpeechColumns, available: number, before: number, after: number): { head: Segment[]; tail: Segment[] } | null {
  if (available < before) return null;
  const heights = segments.map((s) => pieces(s, columns).length);
  const total = heights.reduce((a, b) => a + b, 0);
  let best: { head: Segment[]; tail: Segment[] } | null = null;
  let used = 0;
  for (let k = 0; k < segments.length; k++) {
    const segment = segments[k];
    if (k > 0 && segments[k - 1].kind !== 'parenthetical' && used >= before && used <= available && total - used >= after) {
      best = { head: segments.slice(0, k), tail: segments.slice(k) };
    }
    if (segment.kind !== 'parenthetical') {
      const rest = heights.slice(k + 1).reduce((a, b) => a + b, 0);
      for (const offset of sentenceEnds(segment.runs)) {
        const [h, t] = cut(segment.runs, offset);
        const head: Segment = { runs: h, kind: segment.kind }, tail: Segment = { runs: t, kind: segment.kind };
        const lines = used + pieces(head, columns).length;
        if (lines > available) break;
        if (lines >= before && pieces(tail, columns).length + rest >= after) {
          best = { head: [...segments.slice(0, k), head], tail: [tail, ...segments.slice(k + 1)] };
        }
      }
    }
    used += heights[k];
    if (used > available) break;
  }
  return best;
}

export interface StageLayout {
  pages: ScriptPage[];
  /** Which of the script's parts each page belongs to (for its act–scene–page number). */
  pageParts: number[];
  linesPerPage: number;
}

/** `StageLayout.init`. */
export function stageLayout(script: StageScript, pageHeight = 11): StageLayout {
  const linesPerPage = linesForPage(pageHeight);
  const p = new Paginator(linesPerPage);
  const owners: number[] = [];
  const c = STAGE_COLUMNS;
  const continued = (name: string) => [piece(c.cue, [run(`${name} (Continued)`)])];
  script.parts.forEach((part, index) => {
    p.newPage();
    const first = p.pages.length - 1;
    part.headings.forEach((heading, i) => p.place([piece(c.cue, [run(heading)])], i === 0 ? 0 : 1));
    for (const unit of stageUnits(part.paragraphs)) {
      switch (unit.type) {
        case 'pageBreak': p.newPage(); break;
        case 'block':
          if (unit.lines.length > p.room(1) && unit.lines.length <= p.capacity) p.newPage();
          p.place(unit.lines, 1);
          break;
        case 'action': p.placeAction(unit.segments, c, 1); break;
        case 'speech': p.placeSpeech(unit.speech, 1, null, continued); break;
        case 'dual':
          if (Math.max(height(unit.left), height(unit.right)) > p.capacity) {
            for (const s of [unit.left, unit.right]) {
              const cue = wrap(s.cue.flatMap((x) => x.runs), c.cueWidth).map((r) => piece(c.cue, r));
              p.placeSpeech({ name: s.name, cue, segments: s.segments, columns: c }, 1, null, continued);
            }
          } else {
            p.placeSideBySide(unit.left, unit.right, 1);
          }
          break;
      }
    }
    if (part.end !== null) p.place([piece(c.cue, [run(part.end)])], 1);
    for (let k = 0; k < p.pages.length - first; k++) owners.push(index);
  });
  const pages = p.finished();
  return { pages, pageParts: owners.slice(0, pages.length), linesPerPage };
}

/** `StageLayout.pageCount(fountain:)`: the pages a play's scenes would print on (Write's footer), as a Letter
 *  manuscript, without the pages before it. */
export function stagePageCount(texts: readonly string[]): number {
  const parts = texts.filter((t) => !blank(t)).map((t): StagePart =>
    ({ act: null, scene: null, headings: [], paragraphs: fountainParagraphs(t).map(cloneParagraph), end: null }));
  if (!parts.length) return 0;
  return stageLayout({ ...emptyStageScript(), parts }).pages.length;
}

/** `StageLayout.bracketed`: a direction as printed, in brackets unless the writer bracketed it already. */
function bracketed(lines: Run[][]): Run[][] {
  const text = trimSpaces(lines.map((l) => l.map((r) => r.text).join('')).join('\n'));
  if (!lines.length || text === '' || (text.startsWith('(') && text.endsWith(')'))) return lines;
  const out = lines.map((l) => [...l]);
  out[0].unshift(run('('));
  out[out.length - 1].push(run(')'));
  return out;
}

/** `StageLayout.label`: "Setting:" or "At rise:" opening a scene's direction: the label at the margin, the description
 *  hanging beside it. */
export function directionLabel(line: Run[]): { label: string; rest: Run[] } | null {
  const text = line.map((r) => r.text).join('');
  for (const label of ['Setting:', 'At rise:', 'At Rise:', 'SETTING:', 'AT RISE:']) {
    if (text.startsWith(label)) return { label: label.toUpperCase(), rest: cut(line, count(label))[1] };
  }
  return null;
}

/** Swift's `padding(toLength:withPad: " ")`: padded with spaces, or cut. */
const padded = (s: string, n: number) => { const c = [...s]; return c.length >= n ? c.slice(0, n).join('') : s + ' '.repeat(n - c.length); };

function stageUnits(paragraphs: readonly Paragraph[]): Unit[] {
  const c = STAGE_COLUMNS;
  const units: Unit[] = [];
  const speechElements = new Set<Element>(['dialogue', 'parenthetical', 'lyric']);
  const segmentsFrom = (start: number): [Segment[], number] => {
    const segments: Segment[] = [];
    let j = start;
    while (j < paragraphs.length && speechElements.has(paragraphs[j].element)) {
      const p = paragraphs[j];
      switch (p.element) {
        case 'parenthetical': segments.push(...p.lines.map((runs) => ({ runs, kind: 'parenthetical' as const }))); break;
        case 'lyric': segments.push(...p.lines.map((l) => ({ runs: l.map((r) => ({ ...r, text: r.text.toUpperCase() })), kind: 'lyric' as const }))); break;
        default: segments.push(...p.lines.map((runs) => ({ runs, kind: 'dialogue' as const })));
      }
      j += 1;
    }
    return [segments, j];
  };
  const speechAt = (start: number, columns: SpeechColumns): [Speech, number] => {
    const cue = paragraphs[start];
    const [segments, next] = segmentsFrom(start + 1);
    const runs = (cue.lines[0] ?? []).map((r) => ({ ...r, text: r.text.replaceAll('^', '') }));
    const cuePieces = wrap(runs, columns.cueWidth).map((r) => piece(columns.cue, r));
    return [{ name: speaker(fountainParagraphText(cue)), cue: cuePieces, segments, columns }, next];
  };
  let i = 0;
  while (i < paragraphs.length) {
    const p = paragraphs[i];
    switch (p.element) {
      case 'pageBreak':
        units.push({ type: 'pageBreak' });
        i += 1;
        break;
      case 'character': {
        const [, next] = segmentsFrom(i + 1);
        if (next < paragraphs.length && paragraphs[next].element === 'character' && paragraphs[next].isDual) {
          const [left, afterLeft] = speechAt(i, { ...DUAL_LEFT, lyricItalic: false });
          const [right, afterRight] = speechAt(afterLeft, { ...DUAL_RIGHT, lyricItalic: false });
          units.push({ type: 'dual', left, right });
          i = afterRight;
        } else {
          const [speech, after] = speechAt(i, c);
          units.push({ type: 'speech', speech });
          i = after;
        }
        break;
      }
      case 'dialogue': case 'parenthetical': case 'lyric': {
        const [segments, after] = segmentsFrom(i);
        units.push({ type: 'speech', speech: { name: '', cue: [], segments, columns: c } });
        i = after;
        break;
      }
      case 'action': {
        const labelled = p.lines.length ? directionLabel(p.lines[0]) : null;
        if (labelled) {
          // The label at the margin, its description hanging at the directions' column beside it.
          const list = [labelled.rest, ...p.lines.slice(1)].flatMap((line) => wrap(line, c.actionWidth).map((r) => piece(c.action, r)));
          list[0] = piece(0, [run(padded(labelled.label, c.action)), ...list[0].runs]);
          units.push({ type: 'block', element: 'action', lines: list });
        } else {
          units.push({ type: 'action', segments: bracketed(p.lines).map((runs) => ({ runs, kind: 'action' as const })) });
        }
        i += 1;
        break;
      }
      case 'transition':
        units.push({ type: 'block', element: 'transition', lines: p.lines.flatMap((l) => wrap(l, c.cueWidth).map((r) => piece(c.cue, r))) });
        i += 1;
        break;
      case 'centered':
        units.push({ type: 'block', element: 'centered', lines: p.lines.flatMap((l) => wrap(l, WIDTH).map((r) => piece(Math.trunc((WIDTH - length(r)) / 2), r))) });
        i += 1;
        break;
      default:
        // A scene heading the writer typed (INT. …), or anything else printed: at the margin, as written.
        units.push({ type: 'block', element: p.element, lines: p.lines.flatMap((l) => wrap(l, WIDTH).map((r) => piece(0, r))) });
        i += 1;
    }
  }
  return units;
}
